import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Heading,
  TextInput,
  Logo,
} from '@insight-health-ai/roseate/components';
import { toast } from '@insight-health-ai/roseate/components/toast';
import OTPInput from 'react-otp-input';
import { useChatValidationStore } from 'src/hooks/chat/useChatValidationStore';
import classNames from 'classnames';
import { generateOtpApi } from 'src/api';

type Props = {
  open: boolean;
  onSubmit: (data: any) => void;
  onClose: () => void;
};

const ValidateOtp = ({ open, onSubmit, onClose }: Props) => {
  const { token } = useChatValidationStore();
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const generateOtp = useCallback(() => {
    clearInterval(intervalRef.current!);
    intervalRef.current = setInterval(() => {
      setResendTimer((prev) => (prev > 0 ? prev - 1 : prev));
    }, 1000);
    setLoading(true);
    generateOtpApi({
      chatToken: token!,
    })
      .then(() => {})
      .catch(() => {
        toast.error(
          `Unable to send SMS to your mobile. Please contact your care team for help`
        );
      })
      .finally(() => {
        setResendTimer(60);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (otp.length > 5) {
      onSubmit(otp);
    }
  }, [otp]);

  useEffect(() => {
    generateOtp();
    return () => {
      clearInterval(intervalRef.current!);
    };
  }, []);

  const resendDisabled = loading || resendTimer > 0;
  return (
    <Modal
      title={
        <div className="flex items-center justify-center">
          <Logo className="h-8" />
        </div>
      }
      open={open}
      onClose={onClose}
      footer={null}
    >
      <div className="text-center flex flex-col mt-8">
        <Heading level={4}>Confirmation Code</Heading>
        <div className="text-sm text-secondary mb-6">
          We've sent a code to your mobile phone
        </div>
        <OTPInput
          inputStyle={{ width: '3rem', height: '3rem' }}
          containerStyle={{ gap: '0.5rem', justifyContent: 'center' }}
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderInput={(props: any) => (
            <TextInput disabled={loading} {...props} autoComplete="off" />
          )}
        />
        <div className="text-sm text-secondary mt-2 mb-2">
          Didn't receive one?
          <span
            className={classNames({
              'ml-1 text-primary underline': true,
              'opacity-50 cursor-not-allowed': resendDisabled,
              'cursor-pointer': !resendDisabled,
            })}
            onClick={resendDisabled ? undefined : generateOtp}
          >
            Resend code {resendTimer > 0 && `(${resendTimer}s)`}
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default ValidateOtp;
